import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { C } from '../../constants/colors.js';
import { F, N } from '../../constants/fonts.js';

const TEAL = C.teal || C.secondary;
const CORAL = C.coral || C.primary;
const NAVY = C.navy || C.text;
const WARM_WHITE = C.warmWhite || C.bg;

const CONCERN_MODE_MAP = {
  'Struggles to sound out new words': [
    { icon: '📖', title: 'Phonics', desc: 'Building sound foundations' },
  ],
  'Reads slowly or word-by-word': [
    { icon: '📖', title: 'Phonics', desc: 'Improving decoding speed and flow' },
    { icon: '📚', title: 'Stories', desc: 'Guided fluency practice in context' },
  ],
  "Forgets words they've seen before": [
    { icon: '👁', title: 'Sight Words', desc: 'Smart review for retention' },
  ],
  "Doesn't enjoy reading": [
    { icon: '🫧', title: 'Bubble Pop', desc: 'Playful reading reps that feel fun' },
    { icon: '📚', title: 'Stories', desc: 'Short wins that build confidence' },
  ],
  'Behind where they should be': [
    { icon: '📖', title: 'Phonics', desc: 'Closing core decoding gaps' },
    { icon: '👁', title: 'Sight Words', desc: 'High-frequency word mastery' },
  ],
  'Supplement school or homeschool': [
    { icon: '📖', title: 'Phonics', desc: 'Sound-to-word accuracy' },
    { icon: '👁', title: 'Sight Words', desc: 'Faster automatic recognition' },
    { icon: '📚', title: 'Stories', desc: 'Comprehension through reading' },
  ],
  'Loves reading — wants to go further': [
    { icon: '📚', title: 'Stories', desc: 'More challenging text journeys' },
    { icon: '👁', title: 'Sight Words', desc: 'Vocabulary expansion' },
  ],
  'Just exploring': [
    { icon: '📖', title: 'Phonics', desc: 'Finds the right starting level' },
    { icon: '👁', title: 'Sight Words', desc: 'Builds quick early momentum' },
    { icon: '📚', title: 'Stories', desc: 'Connects practice to meaning' },
  ],
};

const AGE_PREDICTIONS = {
  '4-5': 'recognizing all 26 letter sounds',
  '6-7': 'reading 20+ new sight words',
  '8-9': 'reading chapter books independently',
  '10-12': 'tackling grade-level texts with confidence',
};

function normalizeAgeBracket(ageBracket, profileAge) {
  if (ageBracket === '4-5' || ageBracket === 'early') return '4-5';
  if (ageBracket === '6-7' || ageBracket === 'rising') return '6-7';
  if (ageBracket === '8-9') return '8-9';
  if (ageBracket === '10-12' || ageBracket === 'champion') return '10-12';
  if (profileAge <= 5) return '4-5';
  if (profileAge <= 7) return '6-7';
  if (profileAge <= 9) return '8-9';
  return '10-12';
}

function getReadingLevelBadge(level) {
  if (level <= 2) return { label: 'Emerging Reader', stars: '⭐' };
  if (level <= 4) return { label: 'Rising Decoder', stars: '⭐⭐' };
  if (level <= 6) return { label: 'Fluency Builder', stars: '⭐⭐⭐' };
  if (level <= 8) return { label: 'Story Explorer', stars: '⭐⭐⭐⭐' };
  return { label: 'Reading Champion', stars: '⭐⭐⭐⭐⭐' };
}

function clampScore(v) {
  return Math.max(12, Math.min(99, Math.round(v)));
}

function deriveSkillScores(tracker) {
  const level = tracker?.currentLevel || 1;
  const ms = tracker?.ms || {};
  const totals = {
    phonics: ms?.phonics?.t || 0,
    sight: ms?.sight?.t || 0,
    stories: ms?.stories?.t || 0,
    bubbles: ms?.bubbles?.t || 0,
  };
  const attempts = totals.phonics + totals.sight + totals.stories + totals.bubbles;

  if (attempts > 0) {
    const phonics = totals.phonics > 0 ? (ms.phonics.c / totals.phonics) * 100 : (level / 15) * 100;
    const sight = totals.sight > 0 ? (ms.sight.c / totals.sight) * 100 : (level / 15) * 84;
    const listeningBase = totals.stories + totals.bubbles;
    const listeningCorrect = (ms?.stories?.c || 0) + (ms?.bubbles?.c || 0);
    const listening = listeningBase > 0 ? (listeningCorrect / listeningBase) * 100 : (level / 15) * 90;
    return {
      phonics: clampScore(phonics),
      sight: clampScore(sight),
      listening: clampScore(listening),
    };
  }

  return {
    phonics: clampScore((level / 15) * 100 + 6),
    sight: clampScore((level / 15) * 85 + 4),
    listening: clampScore((level / 15) * 90 + 5),
  };
}

function uniqFocusAreas(concerns = []) {
  const seen = new Set();
  const list = [];
  concerns.forEach((concern) => {
    const items = CONCERN_MODE_MAP[concern] || [];
    items.forEach((item) => {
      if (seen.has(item.title)) return;
      seen.add(item.title);
      list.push(item);
    });
  });
  if (list.length === 0) {
    return [
      { icon: '📖', title: 'Phonics', desc: 'Building reading foundations' },
      { icon: '👁', title: 'Sight Words', desc: 'Smart review for retention' },
      { icon: '📚', title: 'Stories', desc: 'Comprehension through reading' },
    ];
  }
  return list.slice(0, 3);
}

async function generateShareCard(profile, onboardingData, badge, scores, prediction, ageLabel) {
  const heroName = profile?.heroName || 'Explorer';
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1920;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const bgGrad = ctx.createLinearGradient(0, 0, 0, 1920);
  bgGrad.addColorStop(0, '#0F2B3C');
  bgGrad.addColorStop(1, '#0A1E2A');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, 1080, 1920);

  ctx.fillStyle = '#FFFFFF';
  ctx.font = '700 64px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('finley.', 540, 150);

  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.font = '500 34px sans-serif';
  ctx.fillText('READING PROFILE', 540, 205);

  const cardX = 80;
  const cardY = 280;
  const cardW = 920;
  const cardH = 1120;
  const cardGrad = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY + cardH);
  cardGrad.addColorStop(0, '#0F2B3C');
  cardGrad.addColorStop(1, '#143548');
  ctx.fillStyle = cardGrad;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, cardW, cardH, 42);
  ctx.fill();
  ctx.strokeStyle = 'rgba(13,148,136,0.35)';
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.fillStyle = '#0D9488';
  ctx.beginPath();
  ctx.roundRect(130, 340, 360, 72, 36);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '700 34px sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(`${badge.stars} ${badge.label}`, 158, 387);

  ctx.fillStyle = 'rgba(255,255,255,0.88)';
  ctx.font = '500 34px sans-serif';
  ctx.fillText(`Ages ${ageLabel} · ${onboardingData?.readingStage || 'Personalized Plan'}`, 130, 468);

  const bars = [
    { label: 'PHONICS', value: scores.phonics, color: '#0D9488' },
    { label: 'SIGHT', value: scores.sight, color: '#E8614D' },
    { label: 'LISTEN', value: scores.listening, color: '#7BA5C7' },
  ];
  bars.forEach((bar, idx) => {
    const x = 130 + idx * 270;
    const y = 560;
    const w = 190;
    const h = 320;
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 24);
    ctx.fill();

    const fillH = ((h - 74) * bar.value) / 100;
    ctx.fillStyle = bar.color;
    ctx.beginPath();
    ctx.roundRect(x + 26, y + h - 36 - fillH, w - 52, fillH, 16);
    ctx.fill();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = '700 52px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${bar.value}%`, x + w / 2, y + 86);
    ctx.font = '600 26px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText(bar.label, x + w / 2, y + h - 14);
  });

  ctx.fillStyle = '#FFFFFF';
  ctx.font = '600 40px sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(`In 30 days, ${heroName} could be`, 130, 980);
  ctx.fillStyle = '#A4F0E8';
  ctx.font = '700 46px sans-serif';
  ctx.fillText(prediction, 130, 1040);

  ctx.fillStyle = 'rgba(255,255,255,0.72)';
  ctx.font = '600 36px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('finleyreads.com', 540, 1835);

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

export function ReadingDNA({ profile, tracker, onboardingData, onContinue }) {
  const [sharing, setSharing] = useState(false);
  const level = tracker?.currentLevel || 1;
  const badge = getReadingLevelBadge(level);
  const scores = deriveSkillScores(tracker);
  const normalizedAge = normalizeAgeBracket(onboardingData?.ageBracket || profile?.ageBracket, profile?.age || 7);
  const prediction = AGE_PREDICTIONS[normalizedAge] || 'building a strong reading foundation';
  const focusAreas = useMemo(() => uniqFocusAreas(onboardingData?.concerns || []), [onboardingData?.concerns]);
  const ageLabel = normalizedAge;
  const heroName = profile?.heroName || 'Explorer';

  const handleShare = async () => {
    setSharing(true);
    try {
      const blob = await generateShareCard(profile, onboardingData, badge, scores, prediction, ageLabel);
      if (!blob) return;
      const file = new File([blob], 'reading-profile.png', { type: 'image/png' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${heroName}'s Reading Profile`,
          text: `Check out ${heroName}'s reading profile on Finley!`,
          files: [file],
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'reading-profile.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      // User canceled share or browser blocked it.
    } finally {
      setSharing(false);
    }
  };

  return (
    <div style={{ maxWidth: 460, margin: '0 auto', padding: '22px 18px 28px' }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} style={{ textAlign: 'center', marginBottom: 14 }}>
        <div style={{ fontFamily: F, fontSize: 22, color: NAVY, lineHeight: 1 }}>🐬 FINLEY</div>
        <div style={{ fontFamily: N, fontSize: 12, letterSpacing: 1.8, color: 'rgba(15,43,60,0.6)' }}>READING PROFILE</div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
        style={{
          background: 'linear-gradient(135deg, #0F2B3C 0%, #143548 100%)',
          border: '1px solid rgba(13, 148, 136, 0.3)',
          borderRadius: 20,
          padding: 22,
          boxShadow: '0 8px 26px rgba(0,0,0,0.25)',
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'inline-block', background: TEAL, borderRadius: 999, padding: '6px 12px', color: '#fff', fontFamily: F, fontSize: 14, marginBottom: 8 }}>
          {badge.stars} {badge.label}
        </div>
        <div style={{ fontFamily: N, color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 14 }}>
          Ages {ageLabel} · {onboardingData?.readingStage || 'Personalized Reader'}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { key: 'phonics', label: 'PHON', value: scores.phonics, color: TEAL, delay: 0.2 },
            { key: 'sight', label: 'SGHT', value: scores.sight, color: CORAL, delay: 0.35 },
            { key: 'listening', label: 'LIST', value: scores.listening, color: '#7BA5C7', delay: 0.5 },
          ].map((item) => (
            <div key={item.key} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 8px' }}>
              <div style={{ textAlign: 'center', fontFamily: F, color: '#fff', fontSize: 18 }}>{item.value}%</div>
              <div style={{ height: 8, background: 'rgba(255,255,255,0.18)', borderRadius: 999, margin: '8px 0 6px', overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${item.value}%` }}
                  transition={{ duration: 0.8, delay: item.delay, ease: 'easeOut' }}
                  style={{ height: '100%', background: item.color }}
                />
              </div>
              <div style={{ textAlign: 'center', fontFamily: N, color: 'rgba(255,255,255,0.72)', fontSize: 11 }}>{item.label}</div>
            </div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.95 }}>
          <div style={{ fontFamily: N, color: '#fff', fontSize: 16, lineHeight: 1.4 }}>
            In 30 days, {heroName} could be
          </div>
          <div style={{ fontFamily: F, color: '#A4F0E8', fontSize: 20, lineHeight: 1.25 }}>{prediction}</div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.1, ease: 'easeOut' }}
        style={{
          background: WARM_WHITE,
          borderRadius: 16,
          padding: 24,
          boxShadow: '0 4px 12px rgba(15,43,60,0.08)',
          marginBottom: 14,
        }}
      >
        <div style={{ fontFamily: F, color: NAVY, fontSize: 18, marginBottom: 10 }}>What Finley will work on:</div>
        <div style={{ display: 'grid', gap: 9 }}>
          {focusAreas.map((item) => (
            <div key={item.title} style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 10 }}>
              <div style={{ fontSize: 19 }}>{item.icon}</div>
              <div>
                <div style={{ fontFamily: F, color: NAVY, fontSize: 14 }}>{item.title}</div>
                <div style={{ fontFamily: N, color: 'rgba(15,43,60,0.7)', fontSize: 13 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 1.45 }} style={{ display: 'grid', gap: 10 }}>
        <button
          onClick={handleShare}
          disabled={sharing}
          style={{
            width: '100%',
            borderRadius: 12,
            border: `2px solid ${CORAL}`,
            background: 'transparent',
            color: CORAL,
            padding: '14px 16px',
            fontFamily: F,
            fontSize: 16,
            cursor: sharing ? 'not-allowed' : 'pointer',
            opacity: sharing ? 0.6 : 1,
          }}
        >
          {sharing ? 'Preparing...' : 'Share Reading Profile'}
        </button>

        <button
          onClick={onContinue}
          style={{
            width: '100%',
            borderRadius: 12,
            border: 'none',
            background: TEAL,
            color: '#fff',
            padding: '14px 16px',
            fontFamily: F,
            fontSize: 16,
            cursor: 'pointer',
          }}
        >
          🚀 Start Reading
        </button>
      </motion.div>
    </div>
  );
}
